import {
  Box,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Chip,
  Divider,
  Button,
  Collapse,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBackRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  DownloadRounded,
  CheckCircleRounded,
  AutoAwesomeRounded,
  ExpandMoreRounded,
  PictureAsPdfRounded,
  ContentCopyRounded,
  ReplayRounded,
  HistoryRounded,
  ArticleRounded,
  ChatBubbleOutlineRounded,
  ChecklistRounded,
  GradingRounded,
  PersonRounded,
  SearchRounded,
} from '@mui/icons-material';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  useTenantStore,
  useTenantTimeZone,
  useTenantDateFormat,
  useTenantTimeFormat,
} from '../../stores/tenant.store';
import { formatInstant } from '@dosfilos/shared';
import { Markdown } from '../../components/Markdown';
import { QuizReviewPanel } from './components/QuizReviewPanel';
import { errorText } from '../../lib/error-text';
import { usePermissions } from '../../hooks/use-permissions';
import { GRADEBOOK_WRITER_ROLES } from './curso-tabs';
import {
  assignmentApi,
  type AssignmentSummaryDto,
  type SubmissionDto,
  type AssistedReviewDto,
  type AssistedReviewIssueDto,
  type PdfAnnotationDto,
  type RubricCriterionDto,
  type RubricAssessmentEntryDto,
  type FormativeBreakdownDto,
} from '../../lib/api/assignment.api';
import { formatDate, formatDateTime } from '../../lib/i18n-format';

// Lazy so pdfjs (heavy) only loads when a PDF submission is opened.
const PdfViewer = lazy(() => import('./components/PdfViewer'));

interface SubmissionRow {
  /** Vacío cuando el alumno NO entregó: no hay documento todavía. */
  id: string;
  studentId: string;
  studentName: string;
  status: string;
  submittedAt?: string;
  graded?: boolean;
  /** El alumno no entregó nada. La fila existe igual, porque también hay que
   *  poder calificarlo. */
  noDelivery?: boolean;
  /** Subió o escribió algo y NUNCA apretó enviar.
   *
   *  No es lo mismo que no entregar, y la pantalla los confundía: el filtro
   *  descartaba todos los borradores sin enviar, así que quien había subido un
   *  archivo y olvidado mandarlo aparecía como «Sin entrega», idéntico a quien
   *  nunca abrió el diálogo. El docente no tenía cómo enterarse de que había
   *  trabajo ahí, y la acción masiva se negaba a calificarlo sin decir por qué.
   *
   *  Casi siempre es un malentendido de la interfaz, y se resuelve con un
   *  aviso al alumno — no poniéndole un 0. */
  draftStarted?: boolean;
  /** El documento tiene algo dentro: archivo, texto, enlace o respuestas.
   *
   *  DECIDE QUÉ PANEL SE ABRE, y por eso no basta con `noDelivery`. Un registro
   *  de no-entrega que el alumno reabrió y empezó a escribir sigue marcado
   *  `absent`: enrutado por esa marca, el docente veía el panel que dice «este
   *  alumno no entregó nada» mientras el trabajo estaba ahí, invisible. */
  hasWork?: boolean;
}

type RowState = 'toReview' | 'returned' | 'graded' | 'noDelivery';

function rowState(r: SubmissionRow): RowState {
  // UN SOLO ESTADO PARA UN SOLO HECHO. Un borrador sin enviar tuvo su propio
  // estado por un rato y era una distinción de más: entregar es apretar enviar,
  // así que no haberlo enviado ES «Sin entrega». Que haya algo dentro se ve al
  // abrir la fila, y el panel lo dice — pero no es otro estado de la lista.
  //  — Regla del director, 2026-09-25.
  if (r.noDelivery || r.draftStarted) return r.graded ? 'graded' : 'noDelivery';
  if (r.status === 'graded' || (r.status === 'returned' && r.graded)) return 'graded';
  // Returned by me, or reopened by the student afterwards — both read as "Devuelta"
  // from the teacher's point of view (I returned it).
  if (r.status === 'returned' || (r.status === 'draft' && r.submittedAt)) return 'returned';
  return 'toReview';
}

const EMPTY_BREAKDOWN: FormativeBreakdownDto = { achieved: '', missing: '', nextStep: '' };

// A breakdown is worth persisting/showing only when at least one field has text.
function hasBreakdown(b: FormativeBreakdownDto): boolean {
  return !!(b.achieved.trim() || b.missing.trim() || b.nextStep.trim());
}

export function GradeAssignmentPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();

  const sectionId = searchParams.get('section') ?? '';
  const stateAssignment = (location.state as { assignment?: AssignmentSummaryDto } | null)
    ?.assignment;

  const [assignment, setAssignment] = useState<AssignmentSummaryDto | null>(
    stateAssignment ?? null,
  );
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<RowState | 'all'>('all');
  const [rosterCollapsed, setRosterCollapsed] = useState(false);
  /** Confirmación de guardado. La pantalla avanza sola al alumno siguiente, así
   *  que sin esto el único rastro del guardado quedaba fuera de la vista. */
  const [aviso, setAviso] = useState<string | null>(null);
  /** Hay una relectura del roster en curso SIN spinner de página completa. La
   *  pantalla sigue usable con los datos viejos, pero tiene que decir que está
   *  trabajando: tras la acción masiva quedaban varios segundos de pantalla
   *  quieta y el docente creía que no había pasado nada. */
  const [refrescando, setRefrescando] = useState(false);
  // SOLO LECTURA para quien lee y no corrige: el decano (decisión de Ricardo,
  // PR 3A). Ve la lista y el trabajo entregado, sin las herramientas de
  // corrección. El servidor rechaza igual cualquier corrección suya.
  const { roles: myRoles } = usePermissions();
  const readOnly = !myRoles.some((r) => GRADEBOOK_WRITER_ROLES.includes(r));

  useEffect(() => {
    if (assignment || !currentTenantId || !sectionId || !assignmentId) return;
    assignmentApi
      .list({ tenantId: currentTenantId, sectionId })
      .then(({ items }) => {
        const found = items.find((a) => a.id === assignmentId);
        if (found) setAssignment(found);
      })
      .catch(() => {
        /* surfaced below */
      });
  }, [assignment, currentTenantId, sectionId, assignmentId]);

  // EL ROSTER SE VUELVE A LEER DESPUÉS DE LA ACCIÓN MASIVA, así que la carga
  // deja de vivir dentro del efecto y pasa a ser una función con nombre.
  const cargarRoster = useCallback(
    async (conSpinner: boolean) => {
      if (!currentTenantId || !assignmentId) return;
      if (conSpinner) setLoading(true);
      else setRefrescando(true);
      try {
        // LA LISTA LA ARMA EL SERVIDOR (PR 3A): matrícula ∪ entregas, con
        // nombres. Antes se armaba acá leyendo entregas, matrículas y fichas
        // directo de Firestore, y para eso las reglas tenían que dejar a
        // cualquier docente leer las de todo el seminario. Las reglas de
        // armado —un borrador vacío no es entrega, un retirado no debe el
        // trabajo— viven ahora en ListAssignmentGradingRosterUseCase.
        const { rows: filas } = await assignmentApi.listGradingRoster({
          tenantId: currentTenantId,
          assignmentId,
        });
        setRows(filas);
      } catch (e) {
        setError(errorText(e));
      } finally {
        if (conSpinner) setLoading(false);
        else setRefrescando(false);
      }
    },
    [currentTenantId, assignmentId],
  );

  useEffect(() => {
    void cargarRoster(true);
  }, [cargarRoster]);

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const current = rows[index];
  // Lo que la acción masiva cerraría: los que NO ENTREGARON y todavía no tienen
  // nota. Un borrador sin enviar entra acá, porque entregar es apretar enviar.
  // A quien ya tiene nota no se le toca.
  const sinEntrega = rows.filter((r) => rowState(r) === 'noDelivery');
  const recargar = (): void => {
    void cargarRoster(false);
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Tooltip title={t('assignmentsAdmin.review.back')}>
          <IconButton
            onClick={() =>
              navigate(sectionId ? `/curso/${sectionId}?tab=asignaciones` : '/assignments')
            }
          >
            <ArrowBackRounded />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {assignment?.name ?? t('assignmentsAdmin.review.title')}
          </Typography>
          {assignment && (
            <Typography variant="caption" color="text.secondary">
              {t('assignmentsAdmin.review.subtitle', {
                points: assignment.pointsPossible,
                count: rows.length,
              })}
            </Typography>
          )}
        </Box>
        {rows.length > 0 && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Chip label={`${index + 1} / ${rows.length}`} size="small" variant="outlined" />
            <IconButton
              size="small"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
            >
              <ChevronLeftRounded />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setIndex((i) => Math.min(rows.length - 1, i + 1))}
              disabled={index === rows.length - 1}
            >
              <ChevronRightRounded />
            </IconButton>
          </Stack>
        )}
      </Stack>

      {refrescando && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('assignmentsAdmin.review.readOnly')}
        </Alert>
      )}

      {assignment && !readOnly && sinEntrega.length > 0 && (
        <CerrarSinEntregaBar
          assignment={assignment}
          pendientes={sinEntrega.length}
          // Mientras se relee el roster la barra sigue mostrando la cuenta
          // vieja. Repetir la acción no haría daño, pero apretar un botón que
          // dice «10» cuando ya no quedan diez confunde más de lo que ayuda.
          ocupado={refrescando}
          onListo={(r) => {
            // EL RESULTADO CUENTA LO QUE DEJÓ FUERA. La respuesta ya traía
            // `hasWork` y sólo se mostraba `graded`: si la acción se saltaba a
            // alguien, la pantalla no lo decía y parecía que había fallado.
            const partes = [t('assignmentsAdmin.bulkAbsent.done', { count: r.graded })];
            if (r.hasWork > 0) {
              partes.push(t('assignmentsAdmin.bulkAbsent.skippedWork', { count: r.hasWork }));
            }
            setAviso(partes.join(' '));
            recargar();
          }}
        />
      )}

      {/* SI EL SERVIDOR RECHAZÓ, el aviso de arriba es TODA la respuesta: decir
          además «no hay entregas» sería afirmar algo que no se pudo leer. */}
      {error && rows.length === 0 ? null : rows.length === 0 || !current || !assignment ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {t('assignmentsAdmin.review.noSubmissions')}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {rosterCollapsed ? (
            <Tooltip title={t('assignmentsAdmin.review.showRoster')} placement="right">
              <IconButton
                onClick={() => setRosterCollapsed(false)}
                sx={{
                  alignSelf: 'flex-start',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: { xs: 'none', md: 'inline-flex' },
                }}
              >
                <ChevronRightRounded />
              </IconButton>
            </Tooltip>
          ) : (
            <Roster
              rows={rows}
              index={index}
              filter={filter}
              onFilter={setFilter}
              onPick={setIndex}
              onCollapse={() => setRosterCollapsed(true)}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* EL PANEL SE ELIGE POR SI HAY ALGO QUE MOSTRAR, no por la marca
                de no-entrega: un registro `absent` que el alumno reabrió y
                empezó a escribir tiene trabajo a la vista, y el panel pobre lo
                habría escondido detrás de un «no entregó nada».

                La condición pide ADEMÁS que sea una no-entrega o que no exista
                documento: una entrega vieja sin contenido —que no debería
                existir, pero el dato manda— sigue abriéndose en el panel de
                revisión, como siempre. */}
            {readOnly && !current.hasWork && (current.id === '' || current.noDelivery) ? (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography fontWeight={700} gutterBottom>
                  {current.studentName}
                </Typography>
                <Typography color="text.secondary">
                  {t('assignmentsAdmin.review.readOnlyNoDelivery')}
                </Typography>
              </Paper>
            ) : !current.hasWork && (current.id === '' || current.noDelivery) ? (
              <AbsentStudentPanel
                key={`absent-${current.studentId}`}
                assignment={assignment}
                row={current}
                onGraded={({ puntaje, submissionId }) => {
                  // ANTES ERA MUDO. Al guardar, la pantalla saltaba sola al
                  // alumno siguiente —otro sin entrega, con el formulario en
                  // blanco—, idéntica a la de antes de guardar: el director
                  // la leyó como que no se había guardado nada.
                  const eraCorreccion = !!current.graded;
                  setAviso(
                    t(
                      eraCorreccion
                        ? 'assignmentsAdmin.absent.updatedToast'
                        : 'assignmentsAdmin.absent.savedToast',
                      { name: current.studentName, points: puntaje },
                    ),
                  );
                  // El `id` se guarda ahora y no en la próxima carga: sin él la
                  // fila sigue pareciendo sin registro, y el panel no sabría
                  // que lo que viene después es una corrección.
                  const actualizadas = rows.map((rr, i) =>
                    i === index ? { ...rr, id: submissionId, status: 'graded', graded: true } : rr,
                  );
                  setRows(actualizadas);
                  // Corregir una nota no avanza: el docente vino a ESTE alumno
                  // a propósito y sacarlo de la pantalla le esconde el
                  // resultado de lo que acaba de hacer.
                  if (eraCorreccion) return;
                  // Y al avanzar se respeta el filtro. Con «Sin entrega»
                  // activo, saltar al índice siguiente a secas podía dejar
                  // abierto a un alumno que la lista no está mostrando.
                  const siguiente = actualizadas.findIndex(
                    (rr, i) => i > index && (filter === 'all' || rowState(rr) === filter),
                  );
                  if (siguiente !== -1) {
                    setIndex(siguiente);
                    return;
                  }
                  // SE ACABÓ EL FILTRO. Al calificar al último «Sin entrega» su
                  // chip desaparece de la barra, pero el filtro seguía puesto:
                  // la lista quedaba vacía mientras el panel mostraba a un
                  // alumno que ella no listaba. Se vuelve a «Todas».
                  if (filter !== 'all') setFilter('all');
                }}
              />
            ) : (
              <SubmissionReview
                key={current.id}
                assignment={assignment}
                row={current}
                readOnly={readOnly}
                onGraded={() => {
                  setRows((r) =>
                    r.map((rr, i) => (i === index ? { ...rr, status: 'graded' } : rr)),
                  );
                  if (index < rows.length - 1) setIndex(index + 1);
                }}
                onReturned={() => {
                  setRows((r) =>
                    r.map((rr, i) => (i === index ? { ...rr, status: 'returned' } : rr)),
                  );
                }}
              />
            )}
          </Box>
        </Box>
      )}
      <Snackbar
        open={aviso !== null}
        autoHideDuration={4000}
        onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setAviso(null)}>
          {aviso}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/** LA ACCIÓN MASIVA, OFRECIDA Y NO DISPARADA.
 *
 *  Aparece al abrir la pantalla de corrección —el minuto en que el docente está
 *  pensando en esa tarea, que fue la intuición del director— pero la ejecuta él.
 *  Abrir una pantalla es leer: si además escribiera, abrir la tarea equivocada
 *  calificaría al curso entero, y el rastro llevaría el nombre de quien abrió.
 *
 *  Y DICE SI TODAVÍA PUEDEN ENTREGAR. La fecha de ENTREGA vence mucho antes que
 *  la de CIERRE, así que cerrar la nota no siempre significa que se acabó el
 *  plazo. Cuando aún queda, el docente tiene que saberlo antes de apretar. */
function CerrarSinEntregaBar({
  assignment,
  pendientes,
  ocupado,
  onListo,
}: {
  assignment: AssignmentSummaryDto;
  pendientes: number;
  ocupado: boolean;
  onListo: (r: { graded: number; keptTheirGrade: number; hasWork: number }) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const tenantTz = useTenantTimeZone();
  const tenantFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();
  const [confirmando, setConfirmando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // EL ERROR VIVE EN EL DIÁLOGO, no en la página. Si se pintara detrás, el
  // docente vería el diálogo abierto, el botón habilitado otra vez, y ninguna
  // explicación de por qué no pasó nada.
  const [err, setErr] = useState<string | null>(null);

  const abiertaHasta =
    assignment.lockAt && new Date(assignment.lockAt).getTime() > Date.now()
      ? assignment.lockAt
      : undefined;

  async function cerrar(): Promise<void> {
    if (!currentTenantId || guardando) return;
    setGuardando(true);
    try {
      const r = await assignmentApi.gradeAllAbsent({
        tenantId: currentTenantId,
        assignmentId: assignment.id,
        rawPoints: 0,
      });
      setConfirmando(false);
      onListo(r);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <>
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={ocupado}
            onClick={() => setConfirmando(true)}
          >
            {t('assignmentsAdmin.bulkAbsent.action', { count: pendientes })}
          </Button>
        }
      >
        {t('assignmentsAdmin.bulkAbsent.notice', { count: pendientes })}
        {abiertaHasta && (
          <>
            {' '}
            {t('assignmentsAdmin.bulkAbsent.stillOpen', {
              date: formatInstant(abiertaHasta, tenantTz, tenantFmt, tenantTimeFmt),
            })}
          </>
        )}
      </Alert>

      <Dialog
        open={confirmando}
        onClose={() => {
          setConfirmando(false);
          setErr(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('assignmentsAdmin.bulkAbsent.confirmTitle', { count: pendientes })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('assignmentsAdmin.bulkAbsent.confirmBody', { count: pendientes })}
          </Typography>
          {abiertaHasta && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('assignmentsAdmin.bulkAbsent.confirmStillOpen', {
                date: formatInstant(abiertaHasta, tenantTz, tenantFmt, tenantTimeFmt),
              })}
            </Alert>
          )}
          {err && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {err}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmando(false);
              setErr(null);
            }}
            disabled={guardando}
          >
            {t('assignmentsAdmin.bulkAbsent.cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void cerrar()}
            disabled={guardando}
          >
            {guardando
              ? t('assignmentsAdmin.bulkAbsent.saving')
              : t('assignmentsAdmin.bulkAbsent.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const RO = 'assignmentsAdmin.roster';

// Sólo el color vive acá. Las palabras salen de i18n como todo lo que lee un
// humano — antes estaban escritas en duro en español.
const ROW_STATE_META: Record<
  RowState,
  { color: 'info' | 'warning' | 'success' | 'default' | 'error' }
> = {
  toReview: { color: 'info' },
  returned: { color: 'warning' },
  graded: { color: 'success' },
  noDelivery: { color: 'error' },
};

function Roster({
  rows,
  index,
  filter,
  onFilter,
  onPick,
  onCollapse,
}: {
  rows: SubmissionRow[];
  index: number;
  filter: RowState | 'all';
  onFilter: (f: RowState | 'all') => void;
  onPick: (i: number) => void;
  onCollapse: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const counts = rows.reduce(
    (acc, r) => {
      acc[rowState(r)] += 1;
      return acc;
    },
    // SIN TIPO FORZADO. El `as Record<RowState, number>` de antes tapaba que
    // faltaba `noDelivery`, así que su contador era `undefined + 1` = NaN.
    { toReview: 0, returned: 0, graded: 0, noDelivery: 0 },
  );
  const filters: Array<{ key: RowState | 'all'; label: string; n: number }> = [
    { key: 'all', label: t(`${RO}.all`), n: rows.length },
    { key: 'toReview', label: t(`${RO}.toReview`), n: counts.toReview },
    { key: 'returned', label: t(`${RO}.returned`), n: counts.returned },
    { key: 'graded', label: t(`${RO}.graded`), n: counts.graded },
    // Los que no entregaron y todavía no tienen nota: lo que queda por hacer.
    { key: 'noDelivery', label: t(`${RO}.noDelivery`), n: counts.noDelivery },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        width: { xs: '100%', md: 360 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        position: { md: 'sticky' },
        top: 16,
        maxHeight: { md: 'calc(100vh - 32px)' },
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
          {filters
            .filter((f) => f.key === 'all' || f.n > 0)
            .map((f) => (
              <Chip
                key={f.key}
                size="small"
                label={`${f.label} ${f.n}`}
                color={filter === f.key ? 'primary' : 'default'}
                variant={filter === f.key ? 'filled' : 'outlined'}
                onClick={() => onFilter(f.key)}
                sx={{ height: 22, cursor: 'pointer' }}
              />
            ))}
        </Stack>
        <Tooltip title={t(`${RO}.hide`)}>
          <IconButton
            size="small"
            onClick={onCollapse}
            sx={{ ml: 0.5, display: { xs: 'none', md: 'inline-flex' } }}
          >
            <ChevronLeftRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {rows.map((r, i) => {
          const st = rowState(r);
          if (filter !== 'all' && st !== filter) return null;
          const meta = ROW_STATE_META[st];
          const etiqueta =
            st === 'graded' && r.noDelivery ? t(`${RO}.gradedNoDelivery`) : t(`${RO}.${st}`);
          const active = i === index;
          return (
            <Box
              // POR ALUMNO, NO POR ENTREGA: una fila sin entrega tiene `id`
              // vacío, así que todas compartían la misma clave y React las
              // trataba como la misma fila.
              key={r.studentId}
              onClick={() => onPick(i)}
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: 'divider',
                borderLeft: '3px solid',
                borderLeftColor: active ? 'primary.main' : 'transparent',
                bgcolor: active ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ width: 22, flexShrink: 0, textAlign: 'right' }}
              >
                {i + 1}.
              </Typography>
              <Typography
                variant="body2"
                sx={{ flex: 1, minWidth: 0 }}
                noWrap
                fontWeight={active ? 700 : 400}
              >
                {r.studentName}
              </Typography>
              <Chip
                size="small"
                // Calificado Y sin entrega son ciertas a la vez: el docente ya
                // hizo lo suyo y el alumno sigue debiendo el trabajo. Decir
                // sólo «Calificada» borra la segunda.
                label={etiqueta}
                color={meta.color}
                variant="outlined"
                sx={{ height: 20 }}
              />
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function isPdf(name: string, mime?: string): boolean {
  return mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
}
function isImage(name: string, mime?: string): boolean {
  if (mime?.startsWith('image/')) return true;
  return /\.(png|jpe?g|webp|gif)$/i.test(name);
}
function confidenceColor(c: 'low' | 'medium' | 'high'): 'default' | 'warning' | 'success' {
  return c === 'high' ? 'success' : c === 'medium' ? 'default' : 'warning';
}

const K = 'assignmentsAdmin.assistedReview';
const RK = 'assignmentsAdmin.review';

// ── Review draft (client-side autosave) ──────────────────────────────────────
// The teacher's in-progress verdicts/comments/feedback/points are kept in
// localStorage so they survive a reload or switching students before grading.
interface ReviewDraft {
  points: string;
  feedback: string;
  breakdown?: FormativeBreakdownDto;
  verdicts: Record<string, string>;
  critComments: Record<string, string>;
  savedAt: string;
}
function draftKey(tenantId: string, submissionId: string): string {
  // v2: only persisted after a real teacher edit (see dirtyRef). Bumping the
  // version orphans pre-v2 drafts that were saved just by viewing a submission.
  return `reviewDraft:v2:${tenantId}:${submissionId}`;
}
function loadDraft(tenantId: string, submissionId: string): ReviewDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(tenantId, submissionId));
    return raw ? (JSON.parse(raw) as ReviewDraft) : null;
  } catch {
    return null;
  }
}
function saveDraft(tenantId: string, submissionId: string, d: ReviewDraft): void {
  try {
    localStorage.setItem(draftKey(tenantId, submissionId), JSON.stringify(d));
  } catch {
    /* quota — ignore */
  }
}
function clearDraft(tenantId: string, submissionId: string): void {
  try {
    localStorage.removeItem(draftKey(tenantId, submissionId));
  } catch {
    /* ignore */
  }
}

export type WorkTab = 'review' | 'report' | 'feedback' | 'grade';

/** Panel para un alumno que NO entregó.
 *
 *  Es una pantalla aparte y deliberadamente pobre: no hay documento que abrir,
 *  ni rúbrica que aplicar, ni revisión asistida que pedir. Lo único que se
 *  puede hacer es cerrar la nota, y eso es exactamente lo que ofrece. Meterlo
 *  dentro del panel de corrección obligaría a que cada pieza de ese panel
 *  supiera esquivar un documento inexistente. */
function AbsentStudentPanel({
  assignment,
  row,
  onGraded,
}: {
  assignment: AssignmentSummaryDto;
  row: SubmissionRow;
  onGraded: (r: { puntaje: number; submissionId: string }) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [points, setPoints] = useState('');
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const max = assignment.pointsPossible;

  // YA CALIFICADO SIN ENTREGA: la fila vuelve a este panel en cada carga, así
  // que hay que traer la nota puesta. Si no, el docente ve el campo en blanco
  // y no puede saber si guardó ni corregir un valor equivocado.
  const yaTieneRegistro = row.id !== '';
  const [cargando, setCargando] = useState(yaTieneRegistro);
  // LO QUE ESTE PANEL ACABA DE GUARDAR NO SE VUELVE A PEDIR. Al calificar al
  // último alumno de la lista no hay a quién avanzar, así que el panel se queda
  // montado y su `row.id` pasa de vacío a real: sin esta marca el efecto
  // dispararía una lectura al servidor para traer el valor que el docente tiene
  // delante, y sin spinner —`cargando` ya se inicializó y no vuelve a hacerlo—.
  const recienGuardadoRef = useRef(false);
  useEffect(() => {
    if (!currentTenantId || !yaTieneRegistro) return;
    if (recienGuardadoRef.current) {
      recienGuardadoRef.current = false;
      return;
    }
    let vigente = true;
    assignmentApi
      .get({ tenantId: currentTenantId, submissionId: row.id })
      .then((sub) => {
        if (!vigente) return;
        if (sub.grade) {
          setPoints(String(sub.grade.rawPoints));
          setFeedback(sub.grade.feedback ?? '');
        }
      })
      .catch((e) => {
        if (vigente) setErr(errorText(e));
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [currentTenantId, row.id, yaTieneRegistro]);

  const valor = Number(points.replace(',', '.'));
  const valido = points.trim() !== '' && Number.isFinite(valor) && valor >= 0 && valor <= max;

  async function guardar() {
    if (!currentTenantId || !valido || saving) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await assignmentApi.gradeAbsent({
        tenantId: currentTenantId,
        assignmentId: assignment.id,
        studentId: row.studentId,
        rawPoints: valor,
        ...(feedback.trim() ? { feedback: feedback.trim() } : {}),
      });
      recienGuardadoRef.current = true;
      onGraded({ puntaje: valor, submissionId: r.submissionId });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  if (cargando) {
    return (
      <Box textAlign="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 560 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {row.studentName}
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        {t('assignmentsAdmin.absent.notice')}
      </Alert>
      {yaTieneRegistro && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('assignmentsAdmin.absent.alreadyGraded')}
        </Alert>
      )}
      <Stack spacing={2}>
        <TextField
          label={t('assignmentsAdmin.absent.pointsLabel', { max })}
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          size="small"
          sx={{ maxWidth: 220 }}
          error={points.trim() !== '' && !valido}
        />
        <TextField
          label={t('assignmentsAdmin.absent.feedbackLabel')}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          size="small"
          multiline
          minRows={3}
        />
        {err && <Alert severity="error">{err}</Alert>}
        <Box>
          <Button variant="contained" onClick={() => void guardar()} disabled={!valido || saving}>
            {saving
              ? t('assignmentsAdmin.absent.saving')
              : yaTieneRegistro
                ? t('assignmentsAdmin.absent.update')
                : t('assignmentsAdmin.absent.save')}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('assignmentsAdmin.absent.stillMissing')}
        </Typography>
      </Stack>
    </Box>
  );
}

function SubmissionReview({
  assignment,
  row,
  readOnly = false,
  onGraded,
  onReturned,
}: {
  assignment: AssignmentSummaryDto;
  row: SubmissionRow;
  /** El decano: ve el trabajo y la nota puesta, no las herramientas. */
  readOnly?: boolean;
  onGraded: () => void;
  onReturned: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const aId = assignment.id;
  const maxPoints = assignment.pointsPossible;

  const [sub, setSub] = useState<SubmissionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({});

  const [points, setPoints] = useState('');
  const [feedback, setFeedback] = useState('');
  const [grading, setGrading] = useState(false);

  const [review, setReview] = useState<AssistedReviewDto | null>(null);
  const [rubric, setRubric] = useState<RubricCriterionDto[]>([]);
  const [verdicts, setVerdicts] = useState<Record<string, string>>({});
  const [critComments, setCritComments] = useState<Record<string, string>>({});

  const [workTab, setWorkTab] = useState<WorkTab>(assignment.type === 'quiz' ? 'grade' : 'review');
  // A quiz with open questions consolidates its grade from the review panel (left),
  // so the generic grade surface on the right is hidden to avoid a second, confusing
  // grading path. Reported by the review panel once loaded.
  const [quizHasOpen, setQuizHasOpen] = useState(false);
  const handleQuizLoaded = useCallback((info: { hasOpen: boolean }) => {
    setQuizHasOpen(info.hasOpen);
  }, []);
  const [draftRestored, setDraftRestored] = useState(false);
  const [pdfQuery, setPdfQuery] = useState(''); // an evidence quote to find in the PDF
  // Structured three-part formative summary (seeded from the AI review, editable).
  const [breakdown, setBreakdown] = useState<FormativeBreakdownDto>(EMPTY_BREAKDOWN);

  // Teacher PDF marks for this submission. Seeded from the loaded submission,
  // persisted (debounced) on change. annotationsDirtyRef keeps the initial seed
  // from triggering a redundant save.
  const [annotations, setAnnotations] = useState<PdfAnnotationDto[]>([]);
  // AI-issue keys the teacher dismissed — persisted alongside annotations so a
  // dismissed/deleted issue is never re-proposed as a provisional mark on reload.
  const [dismissedIssueKeys, setDismissedIssueKeys] = useState<string[]>([]);
  const annotationsDirtyRef = useRef(false);

  // Refs guard the AI-prefill / draft-restore interplay (both run async on mount).
  const fbEditedRef = useRef(false); // teacher edited feedback → don't let AI overwrite
  const draftConsumedRef = useRef(false); // a draft was restored → AI must not reset edits
  const hydratedRef = useRef(false); // initial load done → safe to start autosaving
  const dirtyRef = useRef(false); // a real teacher edit happened → autosave a draft

  const markDirty = () => {
    dirtyRef.current = true;
  };

  // Marks feedback as teacher-edited so the AI sync effect stops overwriting it.
  const editFeedback = (v: string) => {
    fbEditedRef.current = true;
    markDirty();
    setFeedback(v);
  };

  // Edit one field of the structured breakdown; marks the draft dirty like feedback.
  const editBreakdownField = (field: keyof FormativeBreakdownDto, v: string) => {
    markDirty();
    setBreakdown((b) => ({ ...b, [field]: v }));
  };

  // Points edited by the teacher (vs. AI auto-fill) → mark dirty.
  const editPoints = (v: string) => {
    markDirty();
    setPoints(v);
  };

  const rubricById = useMemo(() => {
    const m = new Map<string, RubricCriterionDto>();
    for (const c of rubric) m.set(c.id, c);
    return m;
  }, [rubric]);

  const levelScore = (criterionId: string, levelName?: string): number | null => {
    const rc = rubricById.get(criterionId);
    if (!rc?.levels?.length || !levelName) return null;
    const idx = rc.levels.findIndex((l) => l.name.toLowerCase() === levelName.toLowerCase());
    return idx >= 0 ? (idx + 1) / rc.levels.length : null;
  };

  const buildAssessment = (): RubricAssessmentEntryDto[] =>
    review
      ? review.criteria.map((c) => {
          const verdict = verdicts[c.criterionId] ?? c.suggestedLevel;
          const ls = levelScore(c.criterionId, verdict);
          const note = critComments[c.criterionId]?.trim();
          return {
            criterionId: c.criterionId,
            score: ls ?? c.suggestedScore,
            ...(note ? { comment: note } : {}),
          };
        })
      : [];

  // Apply an AI review. keepEdits=true preserves the teacher's restored draft
  // (verdicts/comments) instead of resetting to the AI suggestion.
  const applyReview = (r: AssistedReviewDto, keepEdits = false) => {
    setReview(r);
    if (keepEdits) return;
    const v: Record<string, string> = {};
    const cc: Record<string, string> = {};
    for (const c of r.criteria) {
      if (c.suggestedLevel) v[c.criterionId] = c.suggestedLevel;
      // Seed per-criterion comment with the AI's suggestion so the teacher edits
      // OVER it (instead of an empty field with the text only as a placeholder).
      if (c.gap) cc[c.criterionId] = c.gap;
    }
    setVerdicts(v);
    setCritComments(cc);
    setBreakdown(r.formativeBreakdown ?? EMPTY_BREAKDOWN);
  };

  // ── Load submission (+ restore draft when not yet graded) ──────────────────
  useEffect(() => {
    if (!currentTenantId) return;
    setLoading(true);
    setErr(null);
    assignmentApi
      .get({ tenantId: currentTenantId, submissionId: row.id })
      .then((s) => {
        setSub(s);
        setAnnotations(s.annotations ?? []);
        setDismissedIssueKeys(s.dismissedIssueKeys ?? []);
        annotationsDirtyRef.current = false;
        if (s.grade) {
          setPoints(String(s.grade.rawPoints));
          setFeedback(s.grade.feedback ?? '');
          setBreakdown(s.grade.formativeBreakdown ?? EMPTY_BREAKDOWN);
          fbEditedRef.current = true;
        } else {
          const d = loadDraft(currentTenantId, s.id);
          if (d) {
            setPoints(d.points);
            setFeedback(d.feedback);
            setVerdicts(d.verdicts);
            setCritComments(d.critComments);
            if (d.breakdown) setBreakdown(d.breakdown);
            fbEditedRef.current = true;
            draftConsumedRef.current = true;
            dirtyRef.current = true;
            setDraftRestored(true);
          }
        }
        hydratedRef.current = true;
      })
      .catch((e) => setErr(errorText(e)))
      .finally(() => setLoading(false));
  }, [currentTenantId, row.id]);

  // ── Load rubric + latest review ────────────────────────────────────────────
  useEffect(() => {
    if (!currentTenantId) return;
    assignmentApi
      .getAssignmentRubric({ tenantId: currentTenantId, assignmentId: aId })
      .then(({ criteria }) => setRubric(criteria))
      .catch(() => {
        /* non-fatal */
      });
    assignmentApi
      .getReviews({ tenantId: currentTenantId, submissionId: row.id })
      .then(({ reviews }) => {
        const r = reviews.find((x) => x.status === 'ready');
        if (r) applyReview(r, draftConsumedRef.current);
      })
      .catch(() => {
        /* non-fatal */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId, row.id, aId]);

  // Auto-fill the suggested total once review + rubric are loaded (no manual grade yet).
  useEffect(() => {
    if (review && rubric.length > 0 && sub && !sub.grade && points === '') {
      setPoints(String(computePointsFrom(review, rubric, verdicts, maxPoints)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review, rubric, sub]);

  // ── Autosave draft (debounced) ─────────────────────────────────────────────
  // Only after a genuine teacher edit — never just from viewing (which would
  // otherwise create a draft that masks fresh AI seeding on the next visit).
  useEffect(() => {
    if (!currentTenantId || !sub || sub.grade || !hydratedRef.current || !dirtyRef.current) return;
    const id = setTimeout(() => {
      saveDraft(currentTenantId, sub.id, {
        points,
        feedback,
        breakdown,
        verdicts,
        critComments,
        savedAt: new Date().toISOString(),
      });
    }, 800);
    return () => clearTimeout(id);
  }, [points, feedback, breakdown, verdicts, critComments, currentTenantId, sub]);

  // Persist teacher PDF marks (debounced). Only after a real mark/delete edit,
  // never from the initial seed.
  useEffect(() => {
    if (!currentTenantId || !sub || !annotationsDirtyRef.current) return;
    const id = setTimeout(() => {
      void assignmentApi
        .saveAnnotations({
          tenantId: currentTenantId,
          submissionId: sub.id,
          annotations,
          dismissedIssueKeys,
        })
        .catch((e) => console.error('[saveAnnotations] failed', e));
    }, 800);
    return () => clearTimeout(id);
  }, [annotations, dismissedIssueKeys, currentTenantId, sub]);

  const handleAnnotationsChange = (next: PdfAnnotationDto[]) => {
    annotationsDirtyRef.current = true;
    setAnnotations(next);
  };

  const handleDismissedIssueKeysChange = (next: string[]) => {
    annotationsDirtyRef.current = true;
    setDismissedIssueKeys(next);
  };

  const discardDraft = () => {
    if (currentTenantId && sub) clearDraft(currentTenantId, sub.id);
    setDraftRestored(false);
    draftConsumedRef.current = false;
    fbEditedRef.current = false;
    if (review) {
      const v: Record<string, string> = {};
      const cc: Record<string, string> = {};
      for (const c of review.criteria) {
        if (c.suggestedLevel) v[c.criterionId] = c.suggestedLevel;
        if (c.gap) cc[c.criterionId] = c.gap;
      }
      setVerdicts(v);
      setCritComments(cc);
      setFeedback('');
      setBreakdown(review.formativeBreakdown ?? EMPTY_BREAKDOWN);
      setPoints(String(computePointsFrom(review, rubric, v, maxPoints)));
    } else {
      setVerdicts({});
      setCritComments({});
      setFeedback('');
      setBreakdown(EMPTY_BREAKDOWN);
      setPoints('');
    }
  };

  // Signed URLs for viewable files.
  useEffect(() => {
    if (!currentTenantId || !sub) return;
    const viewable = sub.files.filter(
      (f) => isPdf(f.originalFileName, f.mimeType) || isImage(f.originalFileName, f.mimeType),
    );
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      for (const f of viewable) {
        try {
          const { url } = await assignmentApi.getFileUrl({
            tenantId: currentTenantId,
            submissionId: sub.id,
            storagePath: f.storagePath,
          });
          next[f.storagePath] = url;
        } catch {
          /* skip */
        }
      }
      if (!cancelled) setFileUrls(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentTenantId, sub]);

  const handleGrade = async () => {
    if (!currentTenantId || !sub) return;
    const n = Number(points);
    if (isNaN(n) || n < 0 || n > maxPoints) {
      setErr(t(`${RK}.pointsRange`, { max: maxPoints }));
      return;
    }
    setGrading(true);
    setErr(null);
    try {
      const assessment = buildAssessment();
      await assignmentApi.grade({
        tenantId: currentTenantId,
        submissionId: sub.id,
        rawPoints: n,
        ...(feedback.trim() ? { feedback: feedback.trim() } : {}),
        ...(hasBreakdown(breakdown)
          ? {
              formativeBreakdown: {
                achieved: breakdown.achieved.trim(),
                missing: breakdown.missing.trim(),
                nextStep: breakdown.nextStep.trim(),
              },
            }
          : {}),
        ...(assessment.length > 0 ? { rubricAssessment: assessment } : {}),
        ...(review ? { reviewId: review.id } : {}),
      });
      clearDraft(currentTenantId, sub.id);
      onGraded();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setGrading(false);
    }
  };

  // After a return: reflect it in place (don't advance) so the teacher sees the
  // "Devuelta" state + the message they sent, and mark the row.
  const handleReturned = (note: string) => {
    if (currentTenantId && sub) clearDraft(currentTenantId, sub.id);
    setSub((s) => {
      if (!s) return s;
      const { grade: _grade, ...rest } = s;
      return { ...rest, status: 'returned', returnNote: note };
    });
    onReturned();
  };

  const handleDownload = async (storagePath: string) => {
    if (!currentTenantId || !sub) return;
    try {
      const { url } = await assignmentApi.getFileUrl({
        tenantId: currentTenantId,
        submissionId: sub.id,
        storagePath,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(errorText(e));
    }
  };

  if (loading || !sub) {
    return (
      <Box textAlign="center" py={6}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // EL ALUMNO DEJÓ ALGO Y NUNCA APRETÓ ENVIAR. Se ve el trabajo —para eso está
  // la fila— pero NO se califica: `applyGrade` sólo acepta entregas enviadas y
  // lanzaría un Error crudo, y además calificar un borrador sería darle nota a
  // algo que el alumno no dio por terminado. Lo que corresponde es avisarle.
  const borradorSinEnviar = sub.status === 'draft' && !sub.submittedAt;

  return (
    <SplitLayout
      left={
        assignment.type === 'quiz' && currentTenantId ? (
          <QuizReviewPanel
            tenantId={currentTenantId}
            assignmentId={aId}
            submissionId={sub.id}
            studentName={row.studentName}
            onLoaded={handleQuizLoaded}
          />
        ) : (
          <DocumentPane
            sub={sub}
            row={row}
            fileUrls={fileUrls}
            onDownload={handleDownload}
            pdfQuery={pdfQuery}
            annotations={annotations}
            // En solo lectura las marcas no se tocan: nada queda «sin guardar».
            onAnnotationsChange={readOnly ? () => undefined : handleAnnotationsChange}
            issues={review?.issues ?? []}
            dismissedIssueKeys={dismissedIssueKeys}
            onDismissedIssueKeysChange={readOnly ? () => undefined : handleDismissedIssueKeysChange}
          />
        )
      }
      right={
        readOnly ? (
          <ReadOnlyGrade sub={sub} maxPoints={maxPoints} />
        ) : (
          <WorkPane
            tab={workTab}
            onTab={setWorkTab}
            hideGrade={(assignment.type === 'quiz' && quizHasOpen) || borradorSinEnviar}
            draftNotice={borradorSinEnviar}
            onAbsentGraded={onGraded}
            sub={sub}
            assignment={assignment}
            assignmentId={aId}
            studentName={row.studentName}
            maxPoints={maxPoints}
            err={err}
            setErr={setErr}
            points={points}
            setPoints={editPoints}
            feedback={feedback}
            editFeedback={editFeedback}
            breakdown={breakdown}
            onBreakdownField={editBreakdownField}
            grading={grading}
            onGrade={handleGrade}
            draftRestored={draftRestored}
            onDiscardDraft={discardDraft}
            review={review}
            setReviewFromGenerate={(r, crit) => {
              // Explicit (re)generation → fresh AI defaults; drop any restored draft.
              draftConsumedRef.current = false;
              fbEditedRef.current = false;
              setDraftRestored(false);
              setRubric(crit);
              applyReview(r);
              setPoints(String(computePointsFrom(r, crit, {}, maxPoints)));
            }}
            rubric={rubric}
            rubricById={rubricById}
            verdicts={verdicts}
            onVerdict={(cid, level) => {
              markDirty();
              const next = { ...verdicts, [cid]: level };
              setVerdicts(next);
              if (review) setPoints(String(computePointsFrom(review, rubric, next, maxPoints)));
            }}
            critComments={critComments}
            onCritComment={(cid, txt) => {
              markDirty();
              setCritComments((c) => ({ ...c, [cid]: txt }));
            }}
            onSearchInPdf={setPdfQuery}
            onReturned={handleReturned}
          />
        )
      }
    />
  );
}

/** Lo que ve quien lee y no corrige: el estado, la nota puesta y la
 *  devolución, sin un solo control que escriba. */
function ReadOnlyGrade({ sub, maxPoints }: { sub: SubmissionDto; maxPoints: number }): JSX.Element {
  const { t } = useTranslation('sis');
  const g = sub.grade;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('assignmentsAdmin.review.readOnlyGrade')}
        </Typography>
        {g ? (
          <>
            <Typography variant="h5" fontWeight={700}>
              {g.finalPoints} / {maxPoints}
            </Typography>
            {g.feedback && <Markdown>{g.feedback}</Markdown>}
          </>
        ) : (
          <Typography color="text.secondary">
            {t('assignmentsAdmin.review.readOnlyNoGrade')}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

// Pure recompute used by setters (avoids stale closures).
function computePointsFrom(
  review: AssistedReviewDto,
  rubric: RubricCriterionDto[],
  verdicts: Record<string, string>,
  maxPoints: number,
): number {
  const byId = new Map(rubric.map((c) => [c.id, c]));
  let weighted = 0;
  let total = 0;
  for (const c of review.criteria) {
    const rc = byId.get(c.criterionId);
    const w = rc?.weight ?? 0;
    const verdict = verdicts[c.criterionId] ?? c.suggestedLevel;
    let score = c.suggestedScore;
    if (rc?.levels?.length && verdict) {
      const idx = rc.levels.findIndex((l) => l.name.toLowerCase() === verdict.toLowerCase());
      if (idx >= 0) score = (idx + 1) / rc.levels.length;
    }
    weighted += score * w;
    total += w;
  }
  return total === 0 ? 0 : Math.round((weighted / total) * maxPoints * 10) / 10;
}

// ── Resizable split (Claude-style adjustable panes) ──────────────────────────
function SplitLayout({ left, right }: { left: JSX.Element; right: JSX.Element }): JSX.Element {
  const [pct, setPct] = useState<number>(() => {
    const saved = Number(localStorage.getItem('reviewSplitPct'));
    return saved >= 30 && saved <= 75 ? saved : 58;
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const next = ((e.clientX - rect.left) / rect.width) * 100;
      setPct(Math.min(75, Math.max(30, next)));
    };
    const up = () => {
      if (dragging.current) {
        dragging.current = false;
        document.body.style.userSelect = '';
        localStorage.setItem('reviewSplitPct', String(Math.round(pct)));
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [pct]);

  return (
    <Box
      ref={containerRef}
      sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}
    >
      <Box sx={{ width: { xs: '100%', md: `${pct}%` }, minWidth: 0 }}>{left}</Box>
      <Box
        onPointerDown={() => {
          dragging.current = true;
          document.body.style.userSelect = 'none';
        }}
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          width: 10,
          cursor: 'col-resize',
          flexShrink: 0,
          '&:hover .bar': { bgcolor: 'primary.main' },
        }}
      >
        <Box className="bar" sx={{ width: 3, height: 48, borderRadius: 2, bgcolor: 'divider' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{right}</Box>
    </Box>
  );
}

// ── Left pane: Documento (fijo, sin tabs) ────────────────────────────────────
function DocumentPane({
  sub,
  row,
  fileUrls,
  onDownload,
  pdfQuery,
  annotations,
  onAnnotationsChange,
  issues,
  dismissedIssueKeys,
  onDismissedIssueKeysChange,
}: {
  sub: SubmissionDto;
  row: SubmissionRow;
  fileUrls: Record<string, string>;
  onDownload: (p: string) => void;
  pdfQuery: string;
  annotations: PdfAnnotationDto[];
  onAnnotationsChange: (next: PdfAnnotationDto[]) => void;
  issues: AssistedReviewIssueDto[];
  dismissedIssueKeys: string[];
  onDismissedIssueKeysChange: (next: string[]) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const tenantTz = useTenantTimeZone();
  const tenantFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();
  const pdfFile = sub.files.find((f) => isPdf(f.originalFileName, f.mimeType));
  const imageFiles = sub.files.filter((f) => isImage(f.originalFileName, f.mimeType));
  const otherFiles = sub.files.filter(
    (f) => !isPdf(f.originalFileName, f.mimeType) && !isImage(f.originalFileName, f.mimeType),
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        mr: { md: 1 },
        position: { md: 'sticky' },
        top: 16,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <PersonRounded fontSize="small" color="primary" />
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="primary"
          noWrap
          sx={{ flexShrink: 1, minWidth: 0 }}
        >
          {row.studentName}
        </Typography>
        {sub.status === 'returned' && !sub.grade && (
          <Chip
            size="small"
            color="warning"
            icon={<ReplayRounded sx={{ fontSize: 14 }} />}
            label={t(`${RK}.returnedChip`)}
            sx={{ height: 20 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {row.submittedAt && (
          <Typography variant="caption" color="text.secondary">
            {t(`${RK}.submittedAt`, {
              date: formatInstant(row.submittedAt, tenantTz, tenantFmt, tenantTimeFmt),
            })}
          </Typography>
        )}
      </Stack>

      <Box sx={{ p: 1.5 }}>
        <Stack spacing={2}>
          {pdfFile && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <PictureAsPdfRounded fontSize="small" color="error" />
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {pdfFile.originalFileName}
                </Typography>
                <IconButton size="small" onClick={() => onDownload(pdfFile.storagePath)}>
                  <DownloadRounded fontSize="small" />
                </IconButton>
              </Stack>
              {fileUrls[pdfFile.storagePath] ? (
                <Suspense
                  fallback={
                    <Box textAlign="center" py={6}>
                      <CircularProgress size={20} />
                    </Box>
                  }
                >
                  <PdfViewer
                    url={fileUrls[pdfFile.storagePath]!}
                    query={pdfQuery}
                    mode="annotate"
                    annotations={annotations.filter(
                      (a) => a.fileStoragePath === pdfFile.storagePath,
                    )}
                    onAnnotationsChange={(next) => {
                      // Merge: keep marks on other files, replace this file's set.
                      const others = annotations.filter(
                        (a) => a.fileStoragePath !== pdfFile.storagePath,
                      );
                      onAnnotationsChange([...others, ...next]);
                    }}
                    fileStoragePath={pdfFile.storagePath}
                    issues={issues}
                    dismissedIssueKeys={dismissedIssueKeys}
                    onDismissedIssueKeysChange={onDismissedIssueKeysChange}
                  />
                </Suspense>
              ) : (
                <Box textAlign="center" py={6}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </Box>
          )}
          {imageFiles.map((f) =>
            fileUrls[f.storagePath] ? (
              <img
                key={f.storagePath}
                src={fileUrls[f.storagePath]}
                alt={f.originalFileName}
                style={{ width: '100%', borderRadius: 6 }}
              />
            ) : null,
          )}
          {otherFiles.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                {t(`${RK}.otherFiles`)}
              </Typography>
              <Stack spacing={0.5}>
                {otherFiles.map((f) => (
                  <Stack key={f.storagePath} direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {f.originalFileName}
                    </Typography>
                    <IconButton size="small" onClick={() => onDownload(f.storagePath)}>
                      <DownloadRounded fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
          {sub.url && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                URL
              </Typography>
              <Button href={sub.url} target="_blank" rel="noopener noreferrer" size="small">
                {sub.url}
              </Button>
            </Paper>
          )}
          {sub.textBody && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                {t(`${RK}.submittedText`)}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {sub.textBody}
              </Typography>
            </Paper>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

function levelBadgeColor(
  idx?: number,
  count?: number,
): 'default' | 'error' | 'warning' | 'success' | 'info' {
  if (idx === undefined || !count) return 'default';
  const ratio = count <= 1 ? 1 : idx / (count - 1);
  if (ratio <= 0.25) return 'error';
  if (ratio <= 0.5) return 'warning';
  if (ratio < 1) return 'success';
  return 'info';
}

// ── Informe: rúbrica estilizada con comentario por criterio editable inline ───
function ReportPanel({
  review,
  rubricById,
  verdicts,
  critComments,
  onCritComment,
  studentName,
  assignmentName,
  points,
  maxPoints,
  feedback,
  onGoToFeedback,
}: {
  review: AssistedReviewDto | null;
  rubricById: Map<string, RubricCriterionDto>;
  verdicts: Record<string, string>;
  critComments: Record<string, string>;
  onCritComment: (criterionId: string, txt: string) => void;
  studentName: string;
  assignmentName: string;
  points: string;
  maxPoints: number;
  feedback: string;
  onGoToFeedback: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!review) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="text.secondary" variant="body2">
          {t(`${RK}.reportEmpty`)}
        </Typography>
      </Box>
    );
  }

  const reportCriteria = review.criteria.map((c) => {
    const rc = rubricById.get(c.criterionId);
    const level = verdicts[c.criterionId] ?? c.suggestedLevel ?? '';
    const levels = rc?.levels ?? [];
    const levelIndex = level
      ? levels.findIndex((l) => l.name.toLowerCase() === level.toLowerCase())
      : -1;
    return {
      criterionId: c.criterionId,
      label: rc?.label || c.criterionLabel || c.criterionId,
      weight: rc?.weight ?? 0,
      level,
      ...(levelIndex >= 0 ? { levelIndex } : {}),
      ...(levels.length ? { levelCount: levels.length } : {}),
      comment: critComments[c.criterionId] ?? '',
      placeholder: c.gap ?? '',
    };
  });

  // Effective text used for export (teacher comment, or AI gap as fallback).
  const exportCriteria = reportCriteria.map((c) => ({
    label: c.label,
    weight: c.weight,
    level: c.level,
    ...(c.levelIndex !== undefined ? { levelIndex: c.levelIndex } : {}),
    ...(c.levelCount !== undefined ? { levelCount: c.levelCount } : {}),
    comment: c.comment.trim() || c.placeholder,
  }));

  const asPlainText = (): string => {
    const lines = [`INFORME DE REVISIÓN — ${assignmentName}`, `Estudiante: ${studentName}`];
    if (points !== '') lines.push(`Puntaje: ${points} / ${maxPoints}`);
    lines.push('', 'POR CRITERIO:');
    for (const c of exportCriteria) {
      lines.push(`• ${c.label}${c.level ? ` — ${c.level}` : ''}`);
      if (c.comment) lines.push(`    ${c.comment}`);
    }
    lines.push('', 'RETROALIMENTACIÓN:', feedback);
    return lines.join('\n');
  };

  const copy = () => {
    void navigator.clipboard.writeText(asPlainText());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { downloadReviewReportPdf } = await import('./components/ReviewReportPdf');
      await downloadReviewReportPdf({
        assignmentName,
        studentName,
        points,
        maxPoints,
        criteria: exportCriteria,
        feedback,
        generatedOn: formatDate(new Date(), {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" startIcon={<ContentCopyRounded fontSize="small" />} onClick={copy}>
          {copied ? t(`${RK}.reportCopied`) : t(`${RK}.reportCopy`)}
        </Button>
        <Button
          size="small"
          startIcon={
            downloading ? <CircularProgress size={14} /> : <DownloadRounded fontSize="small" />
          }
          onClick={downloadPdf}
          disabled={downloading}
        >
          PDF
        </Button>
      </Stack>

      {/* Document-style page preview */}
      <Paper
        elevation={3}
        sx={{
          bgcolor: 'background.paper',
          width: '100%',
          p: { xs: 3, md: 5 },
          borderRadius: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'Georgia, serif' }}>
          Informe de revisión
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {assignmentName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Estudiante: {studentName}
        </Typography>
        {points !== '' && (
          <Box
            sx={{
              mt: 2,
              mb: 1,
              px: 1.5,
              py: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              display: 'inline-block',
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Puntaje: {points} / {maxPoints}
            </Typography>
          </Box>
        )}

        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mt: 2, mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}
        >
          Evaluación por criterio
        </Typography>
        <Stack spacing={1.5}>
          {reportCriteria.map((c) => (
            <Box key={c.criterionId}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                  {c.label}
                </Typography>
                {c.level && (
                  <Chip
                    size="small"
                    label={c.level}
                    color={levelBadgeColor(c.levelIndex, c.levelCount)}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  {c.weight}%
                </Typography>
              </Stack>
              <TextField
                multiline
                minRows={2}
                fullWidth
                variant="standard"
                placeholder={c.placeholder || t(`${K}.criterionComment`)}
                InputProps={{ disableUnderline: true, sx: { fontSize: 14, lineHeight: 1.6 } }}
                value={c.comment}
                onChange={(e) => onCritComment(c.criterionId, e.target.value)}
                sx={{ mt: 0.25 }}
              />
            </Box>
          ))}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: 2.5, mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
            Retroalimentación
          </Typography>
          <Button size="small" onClick={onGoToFeedback}>
            {t(`${RK}.editInFeedbackTab`)}
          </Button>
        </Stack>
        {feedback ? (
          <Markdown>{feedback}</Markdown>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
            {t(`${RK}.feedbackEmpty`)}
          </Typography>
        )}
      </Paper>
      <Typography variant="caption" color="text.secondary" textAlign="center">
        {t(`${RK}.reportHint`)}
      </Typography>
    </Stack>
  );
}

// ── Right pane: WorkPane (Documento fijo a la izquierda; aquí los tabs) ───────
interface WorkPaneProps {
  tab: WorkTab;
  onTab: (t: WorkTab) => void;
  /** Quiz with open questions: grade is consolidated from the left review panel, so
   *  hide the generic grade button + panel here (keep Devolver). */
  hideGrade?: boolean;
  /** El trabajo está a la vista pero sin enviar: se explica y no se califica. */
  draftNotice?: boolean;
  /** Avisa que se cerró la nota como no-entrega, para refrescar la fila. */
  onAbsentGraded?: () => void;
  sub: SubmissionDto;
  assignment: AssignmentSummaryDto;
  assignmentId: string;
  studentName: string;
  maxPoints: number;
  err: string | null;
  setErr: (s: string | null) => void;
  points: string;
  setPoints: (s: string) => void;
  feedback: string;
  editFeedback: (s: string) => void;
  breakdown: FormativeBreakdownDto;
  onBreakdownField: (field: keyof FormativeBreakdownDto, v: string) => void;
  grading: boolean;
  onGrade: () => void;
  draftRestored: boolean;
  onDiscardDraft: () => void;
  review: AssistedReviewDto | null;
  setReviewFromGenerate: (r: AssistedReviewDto, crit: RubricCriterionDto[]) => void;
  rubric: RubricCriterionDto[];
  rubricById: Map<string, RubricCriterionDto>;
  verdicts: Record<string, string>;
  onVerdict: (criterionId: string, level: string) => void;
  critComments: Record<string, string>;
  onCritComment: (criterionId: string, txt: string) => void;
  onSearchInPdf: (text: string) => void;
  onReturned: (note: string) => void;
}

function WorkPane(props: WorkPaneProps): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const {
    tab,
    onTab,
    hideGrade,
    draftNotice,
    sub,
    assignment,
    maxPoints,
    err,
    setErr,
    points,
    setPoints,
    feedback,
    editFeedback,
    review,
    draftRestored,
    onDiscardDraft,
  } = props;
  // A quiz is auto-graded: the AI-review / report / feedback tabs don't apply.
  // Only the grade (override) tab is relevant.
  const isQuiz = assignment.type === 'quiz';
  const [returnOpen, setReturnOpen] = useState(false);
  // CERRAR LA NOTA DE UN BORRADOR SIN ENVIAR. Va por `gradeAbsent` y no por el
  // botón normal: `applyGrade` sólo acepta entregas enviadas, y además el
  // registro tiene que quedar marcado como no-entrega para que los informes
  // sigan diciendo que el alumno debe ese trabajo.
  const [cerrandoSinEntrega, setCerrandoSinEntrega] = useState(false);
  const [puntosAusencia, setPuntosAusencia] = useState('0');
  const [guardandoAusencia, setGuardandoAusencia] = useState(false);
  const [errAusencia, setErrAusencia] = useState<string | null>(null);

  const valorAusencia = Number(puntosAusencia.replace(',', '.'));
  // EL BOTÓN NO PUEDE FALLAR EN SILENCIO. Antes un valor fuera de rango hacía
  // que la función se devolviera sin más: el docente apretaba y no pasaba nada.
  const ausenciaValida =
    puntosAusencia.trim() !== '' &&
    Number.isFinite(valorAusencia) &&
    valorAusencia >= 0 &&
    valorAusencia <= assignment.pointsPossible;

  async function cerrarComoSinEntrega(): Promise<void> {
    if (!currentTenantId || guardandoAusencia || !ausenciaValida) return;
    const valor = valorAusencia;
    setGuardandoAusencia(true);
    setErrAusencia(null);
    try {
      await assignmentApi.gradeAbsent({
        tenantId: currentTenantId,
        assignmentId: assignment.id,
        studentId: sub.studentId,
        rawPoints: valor,
      });
      setCerrandoSinEntrega(false);
      props.onAbsentGraded?.();
    } catch (e) {
      setErrAusencia(errorText(e));
    } finally {
      setGuardandoAusencia(false);
    }
  }
  const [returnNote, setReturnNote] = useState('');
  const [returning, setReturning] = useState(false);

  const openReturn = () => {
    setReturnNote(feedback);
    setReturnOpen(true);
  };

  const doReturn = async () => {
    if (!currentTenantId) return;
    setReturning(true);
    try {
      const note = returnNote.trim();
      await assignmentApi.returnForRevision({
        tenantId: currentTenantId,
        submissionId: sub.id,
        ...(note ? { note } : {}),
      });
      setReturnOpen(false);
      setReturnNote('');
      props.onReturned(note);
    } catch (e) {
      setErr(errorText(e, t(`${K}.error`)));
    } finally {
      setReturning(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        position: { md: 'sticky' },
        top: 16,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: { md: 'calc(100vh - 32px)' },
      }}
    >
      {/* Persistent header: score + Devolver + Calificar (from any tab) */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2,
          py: 1,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ flex: 1 }} color="text.secondary">
          {points !== '' ? `${points} / ${maxPoints} pts` : t(`${RK}.noScore`)}
        </Typography>
        {sub.status === 'submitted' && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<ReplayRounded />}
            onClick={openReturn}
          >
            {t(`${RK}.return`)}
          </Button>
        )}
        {!hideGrade && (
          <Button
            size="small"
            variant="contained"
            startIcon={props.grading ? <CircularProgress size={14} /> : <CheckCircleRounded />}
            onClick={props.onGrade}
            disabled={props.grading}
          >
            {props.grading
              ? t(`${RK}.saving`)
              : sub.grade
                ? t(`${RK}.updateGrade`)
                : t(`${RK}.grade`)}
          </Button>
        )}
      </Stack>

      {draftNotice && (
        <Alert
          severity="warning"
          sx={{ m: 1.5, mb: 0 }}
          action={
            props.onAbsentGraded && (
              <Button size="small" color="warning" onClick={() => setCerrandoSinEntrega(true)}>
                {t(`${RK}.closeAsAbsent`)}
              </Button>
            )
          }
        >
          {t(`${RK}.draftNotSent`)}
        </Alert>
      )}

      <Dialog
        open={cerrandoSinEntrega}
        onClose={() => setCerrandoSinEntrega(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t(`${RK}.closeAsAbsentTitle`)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t(`${RK}.closeAsAbsentBody`)}
          </Typography>
          <TextField
            autoFocus
            size="small"
            label={t('assignmentsAdmin.absent.pointsLabel', { max: assignment.pointsPossible })}
            value={puntosAusencia}
            onChange={(e) => setPuntosAusencia(e.target.value)}
            error={puntosAusencia.trim() !== '' && !ausenciaValida}
            sx={{ maxWidth: 220 }}
          />
          {errAusencia && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errAusencia}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCerrandoSinEntrega(false)} disabled={guardandoAusencia}>
            {t(`${K}.cancel`)}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void cerrarComoSinEntrega()}
            disabled={guardandoAusencia || !ausenciaValida}
          >
            {guardandoAusencia ? t('assignmentsAdmin.absent.saving') : t(`${RK}.closeAsAbsent`)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t(`${RK}.returnTitle`)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {t(`${RK}.returnHint`)}
          </Typography>
          <TextField
            autoFocus
            label={t(`${RK}.returnLabel`)}
            placeholder={t(`${RK}.returnPlaceholder`)}
            multiline
            minRows={4}
            fullWidth
            value={returnNote}
            onChange={(e) => setReturnNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnOpen(false)}>{t(`${K}.cancel`)}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={doReturn}
            disabled={returning}
            startIcon={returning ? <CircularProgress size={14} /> : <ReplayRounded />}
          >
            {t(`${RK}.return`)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Body: vertical icon tab-rail + active panel */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Tabs
          orientation="vertical"
          value={tab}
          onChange={(_, v) => onTab(v)}
          sx={{
            borderRight: '1px solid',
            borderColor: 'divider',
            minWidth: 92,
            flexShrink: 0,
            '& .MuiTab-root': { minHeight: 64, fontSize: 11, textTransform: 'none', px: 1 },
          }}
        >
          {!isQuiz && (
            <Tab
              value="review"
              icon={<AutoAwesomeRounded fontSize="small" />}
              label={t(`${RK}.tabReview`)}
            />
          )}
          {!isQuiz && (
            <Tab
              value="report"
              icon={<ArticleRounded fontSize="small" />}
              label={t(`${RK}.tabReport`)}
            />
          )}
          {!isQuiz && (
            <Tab
              value="feedback"
              icon={<ChatBubbleOutlineRounded fontSize="small" />}
              label={t(`${RK}.tabFeedback`)}
            />
          )}
          <Tab
            value="grade"
            icon={<GradingRounded fontSize="small" />}
            label={t(`${RK}.tabGrade`)}
          />
        </Tabs>

        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 2 }}>
          {err && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setErr(null)}>
              {err}
            </Alert>
          )}
          {draftRestored && (
            <Alert
              severity="info"
              sx={{ mb: 1.5 }}
              action={
                <Button color="inherit" size="small" onClick={onDiscardDraft}>
                  {t(`${RK}.draftDiscard`)}
                </Button>
              }
            >
              {t(`${RK}.draftRestored`)}
            </Alert>
          )}
          {sub.status === 'returned' && !sub.grade && (
            <Alert
              severity="warning"
              icon={<ReplayRounded />}
              sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}
            >
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {t(`${RK}.returnedBanner`)}
              </Typography>
              {sub.returnNote || t(`${RK}.returnedNoNote`)}
            </Alert>
          )}
          {sub.revisionHistory && sub.revisionHistory.length > 0 && (
            <Box
              sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <HistoryRounded fontSize="small" color="action" />
                <Typography variant="subtitle2">{t(`${RK}.timelineTitle`)}</Typography>
              </Stack>
              <Stack spacing={1.25}>
                {[...sub.revisionHistory]
                  .sort((a, b) => b.at.localeCompare(a.at))
                  .map((ev, i) => (
                    <Box
                      key={`${ev.at}-${i}`}
                      sx={{ pl: 1.25, borderLeft: '3px solid', borderColor: 'warning.light' }}
                    >
                      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                        <Typography variant="caption" fontWeight={700}>
                          {t(`${RK}.timelineReturned`, { attempt: ev.attempt })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(ev.at)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {ev.note || t(`${RK}.returnedNoNote`)}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </Box>
          )}

          {!isQuiz && tab === 'review' && <ReviewPanel {...props} />}
          {!isQuiz && tab === 'report' && (
            <ReportPanel
              review={review}
              rubricById={props.rubricById}
              verdicts={props.verdicts}
              critComments={props.critComments}
              onCritComment={props.onCritComment}
              studentName={props.studentName}
              assignmentName={assignment.name}
              points={points}
              maxPoints={maxPoints}
              feedback={feedback}
              onGoToFeedback={() => onTab('feedback')}
            />
          )}
          {!isQuiz && tab === 'feedback' && (
            <FeedbackPanel
              feedback={feedback}
              editFeedback={editFeedback}
              breakdown={props.breakdown}
              onBreakdownField={props.onBreakdownField}
              review={review}
            />
          )}
          {tab === 'grade' &&
            (hideGrade ? (
              <Alert severity="info">{t(`${RK}.quizConsolidateHint`)}</Alert>
            ) : (
              <GradePanel
                sub={sub}
                points={points}
                setPoints={setPoints}
                maxPoints={maxPoints}
                feedback={feedback}
              />
            ))}
        </Box>
      </Box>
    </Paper>
  );
}

// ── Retroalimentación: resumen formativo (primario) + nota libre (opcional) ───
function FeedbackPanel({
  feedback,
  editFeedback,
  breakdown,
  onBreakdownField,
  review,
}: {
  feedback: string;
  editFeedback: (s: string) => void;
  breakdown: FormativeBreakdownDto;
  onBreakdownField: (field: keyof FormativeBreakdownDto, v: string) => void;
  review: AssistedReviewDto | null;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const aiBreakdown = review?.formativeBreakdown;
  const [breakdownTab, setBreakdownTab] = useState<keyof FormativeBreakdownDto>('achieved');
  const breakdownFields: { key: keyof FormativeBreakdownDto; labelKey: string }[] = [
    { key: 'achieved', labelKey: 'breakdownAchieved' },
    { key: 'missing', labelKey: 'breakdownMissing' },
    { key: 'nextStep', labelKey: 'breakdownNextStep' },
  ];
  return (
    <Stack spacing={1.5}>
      {/* Primary formative feedback: the structured three-part summary. Seeded
          from the AI review, edited in roomy per-field tabs. */}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <ChecklistRounded fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {t(`${RK}.breakdownTitle`)}
        </Typography>
        {aiBreakdown && (
          <Button
            size="small"
            startIcon={<AutoAwesomeRounded fontSize="small" />}
            onClick={() => {
              onBreakdownField('achieved', aiBreakdown.achieved);
              onBreakdownField('missing', aiBreakdown.missing);
              onBreakdownField('nextStep', aiBreakdown.nextStep);
            }}
          >
            {t(`${RK}.breakdownUseAi`)}
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t(`${RK}.breakdownHint`)}
      </Typography>
      {/* Tabs (one per field) + a roomy multiline area, so each line has space to
          write instead of three cramped single-line inputs. A dot marks filled tabs. */}
      <ToggleButtonGroup
        size="small"
        exclusive
        fullWidth
        value={breakdownTab}
        onChange={(_, v) => {
          if (v) setBreakdownTab(v as keyof FormativeBreakdownDto);
        }}
        aria-label={t(`${RK}.breakdownTitle`)}
      >
        {breakdownFields.map((f) => (
          <ToggleButton key={f.key} value={f.key} sx={{ textTransform: 'none', gap: 0.5 }}>
            {t(`${RK}.${f.labelKey}`)}
            {breakdown[f.key].trim() && (
              <Box
                component="span"
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }}
              />
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        multiline
        minRows={5}
        fullWidth
        placeholder={t(`${RK}.${breakdownFields.find((f) => f.key === breakdownTab)!.labelKey}`)}
        value={breakdown[breakdownTab]}
        onChange={(e) => onBreakdownField(breakdownTab, e.target.value)}
      />

      {/* Optional free-text note — extra prose the teacher wants to add on top of
          the structured summary. Empty by default (no longer AI-seeded). */}
      <Divider sx={{ mt: 1 }} />
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <ChatBubbleOutlineRounded fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {t(`${RK}.feedbackTitle`)}
        </Typography>
      </Stack>
      <TextField
        multiline
        minRows={6}
        fullWidth
        placeholder={t(`${RK}.feedbackPlaceholder`)}
        value={feedback}
        onChange={(e) => editFeedback(e.target.value)}
      />
      <Typography variant="caption" color="text.secondary">
        {t(`${RK}.feedbackOptionalHint`)}
      </Typography>
    </Stack>
  );
}

// ── Calificación: puntaje + resumen de lo que se envía ───────────────────────
function GradePanel({
  sub,
  points,
  setPoints,
  maxPoints,
  feedback,
}: {
  sub: SubmissionDto;
  points: string;
  setPoints: (s: string) => void;
  maxPoints: number;
  feedback: string;
}): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        label={t(`${RK}.pointsLabel`, { max: maxPoints })}
        type="number"
        value={points}
        onChange={(e) => setPoints(e.target.value)}
        inputProps={{ min: 0, max: maxPoints }}
      />
      <Alert severity={feedback.trim() ? 'info' : 'warning'} icon={false}>
        {feedback.trim()
          ? t(`${RK}.gradeSummary`, { points: points || '0' })
          : t(`${RK}.gradeSummaryNoFeedback`, { points: points || '0' })}
      </Alert>
      {sub.grade && (
        <>
          <Divider />
          <Typography variant="caption" color="text.secondary">
            {t(`${RK}.currentGrade`, {
              raw: sub.grade.rawPoints,
              penalty: (sub.grade.latePenalty * 100).toFixed(0),
              final: sub.grade.finalPoints.toFixed(1),
            })}
          </Typography>
        </>
      )}
    </Stack>
  );
}

function ReviewPanel(props: {
  sub: SubmissionDto;
  assignmentId: string;
  maxPoints: number;
  setErr: (s: string | null) => void;
  review: AssistedReviewDto | null;
  setReviewFromGenerate: (r: AssistedReviewDto, crit: RubricCriterionDto[]) => void;
  rubric: RubricCriterionDto[];
  rubricById: Map<string, RubricCriterionDto>;
  verdicts: Record<string, string>;
  onVerdict: (criterionId: string, level: string) => void;
  critComments: Record<string, string>;
  onCritComment: (criterionId: string, txt: string) => void;
  onSearchInPdf: (text: string) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { review, rubricById, verdicts, critComments } = props;
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Config dialog (persisted per assignment).
  const [configOpen, setConfigOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [context, setContext] = useState('');
  const [loadingRubric, setLoadingRubric] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [allCriteria, setAllCriteria] = useState<RubricCriterionDto[]>([]);

  const openConfig = async () => {
    if (!currentTenantId) return;
    props.setErr(null);
    setConfigOpen(true);
    setLoadingRubric(true);
    try {
      const { criteria, config } = await assignmentApi.getAssignmentRubric({
        tenantId: currentTenantId,
        assignmentId: props.assignmentId,
      });
      setAllCriteria(criteria);
      if (config) {
        const byId = new Map(config.criteria.map((c) => [c.criterionId, c]));
        setSelected(
          new Set(criteria.filter((c) => byId.get(c.id)?.enabled !== false).map((c) => c.id)),
        );
        const n: Record<string, string> = {};
        for (const c of config.criteria) if (c.note) n[c.criterionId] = c.note;
        setNotes(n);
        setContext(config.instructions ?? '');
      } else {
        setSelected(new Set(criteria.map((c) => c.id)));
        setNotes({});
      }
    } catch (e) {
      const msg = errorText(e, '');
      props.setErr(/rubric/i.test(msg) ? t(`${K}.noRubric`) : t(`${K}.error`));
      setConfigOpen(false);
    } finally {
      setLoadingRubric(false);
    }
  };

  const saveConfig = async (thenRun: boolean) => {
    if (!currentTenantId) return;
    if (selected.size === 0) {
      props.setErr(t(`${K}.selectAtLeastOne`));
      return;
    }
    setSavingConfig(true);
    try {
      await assignmentApi.update({
        tenantId: currentTenantId,
        assignmentId: props.assignmentId,
        updates: {
          reviewConfig: {
            criteria: allCriteria.map((c) => ({
              criterionId: c.id,
              enabled: selected.has(c.id),
              ...(notes[c.id]?.trim() ? { note: notes[c.id]!.trim() } : {}),
            })),
            ...(context.trim() ? { instructions: context.trim() } : {}),
          },
        },
      });
      setConfigOpen(false);
      if (thenRun) await runReview(true);
    } catch (e) {
      props.setErr(errorText(e, t(`${K}.error`)));
    } finally {
      setSavingConfig(false);
    }
  };

  const runReview = async (force: boolean) => {
    if (!currentTenantId) return;
    setBusy(true);
    props.setErr(null);
    try {
      const { review: r, rubricCriteria } = await assignmentApi.generateReview({
        tenantId: currentTenantId,
        submissionId: props.sub.id,
        force,
      });
      props.setReviewFromGenerate(r, rubricCriteria);
    } catch (e) {
      const msg = errorText(e, '');
      props.setErr(/rubric/i.test(msg) ? t(`${K}.noRubric`) : t(`${K}.error`));
    } finally {
      setBusy(false);
    }
  };

  const toggleCriterion = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const stale = review !== null && review.attempt !== props.sub.attempt;

  const configDialog = (
    <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(`${K}.configTitle`)}
        <Typography variant="caption" color="text.secondary" display="block">
          {t(`${K}.configSubtitle`)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loadingRubric ? (
          <Box textAlign="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t(`${K}.selectCriteria`)}
            </Typography>
            <Stack spacing={1.5}>
              {allCriteria.map((c) => (
                <Box key={c.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.has(c.id)}
                        onChange={() => toggleCriterion(c.id)}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={600}>
                        {c.label}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({c.weight}%)
                        </Typography>
                      </Typography>
                    }
                  />
                  {selected.has(c.id) && (
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={t(`${K}.criterionNotePlaceholder`)}
                      value={notes[c.id] ?? ''}
                      onChange={(e) => setNotes((n) => ({ ...n, [c.id]: e.target.value }))}
                      sx={{ ml: 4, mt: 0.5, width: 'calc(100% - 32px)' }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
            <TextField
              label={t(`${K}.contextLabel`)}
              placeholder={t(`${K}.contextPlaceholder`)}
              multiline
              rows={2}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfigOpen(false)}>{t(`${K}.cancel`)}</Button>
        <Button onClick={() => saveConfig(false)} disabled={loadingRubric || savingConfig}>
          {savingConfig ? t(`${K}.savingConfig`) : t(`${K}.saveConfig`)}
        </Button>
        <Button
          variant="contained"
          onClick={() => saveConfig(true)}
          disabled={loadingRubric || savingConfig || selected.size === 0}
          startIcon={<AutoAwesomeRounded />}
        >
          {t(`${K}.saveAndRun`)}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      {configDialog}

      <Stack direction="row" justifyContent="flex-end" spacing={1} mb={1}>
        <Button size="small" onClick={openConfig} disabled={busy || savingConfig}>
          {t(`${K}.configure`)}
        </Button>
        {review && (
          <Button
            size="small"
            onClick={() => runReview(true)}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : undefined}
          >
            {busy ? t(`${K}.generating`) : t(`${K}.regenerate`)}
          </Button>
        )}
      </Stack>

      {!review && !busy && (
        <Button
          variant="contained"
          fullWidth
          startIcon={<AutoAwesomeRounded />}
          onClick={() => runReview(false)}
        >
          {t(`${K}.generate`)}
        </Button>
      )}

      {busy && !review && (
        <Stack alignItems="center" spacing={1} py={4}>
          <CircularProgress size={24} />
          <Typography variant="caption" color="text.secondary">
            {t(`${K}.generating`)}
          </Typography>
        </Stack>
      )}

      {review && (
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {t(`${K}.disclaimer`)}
          </Typography>
          {stale && <Alert severity="warning">{t(`${K}.staleWarning`)}</Alert>}

          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {review.flags.unsupportedContent && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t(`${K}.flagUnsupported`)}
              />
            )}
            {review.flags.offTopic && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t(`${K}.flagOffTopic`)}
              />
            )}
            {review.flags.missingSections && (
              <Chip size="small" color="warning" variant="outlined" label={t(`${K}.flagMissing`)} />
            )}
            {review.flags.possibleIntegrity && (
              <Chip size="small" color="error" variant="outlined" label={t(`${K}.flagIntegrity`)} />
            )}
          </Stack>

          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              {t(`${K}.criteria`)}
            </Typography>
            <Stack spacing={1} mt={0.5}>
              {review.criteria.map((c) => {
                const rc = rubricById.get(c.criterionId);
                const open = expanded[c.criterionId] ?? false;
                const verdict = verdicts[c.criterionId] ?? c.suggestedLevel ?? '';
                const levels = rc?.levels ?? [];
                return (
                  <Paper key={c.criterionId} variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                        {rc?.label || c.criterionLabel || c.criterionId}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          c.suggestedLevel
                            ? `${t(`${K}.suggested`)}: ${c.suggestedLevel}`
                            : `${Math.round(c.suggestedScore * 100)}%`
                        }
                        color={confidenceColor(c.confidence)}
                        variant="outlined"
                      />
                      <IconButton
                        size="small"
                        onClick={() => setExpanded((e) => ({ ...e, [c.criterionId]: !open }))}
                      >
                        <ExpandMoreRounded
                          fontSize="small"
                          sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                        />
                      </IconButton>
                    </Stack>

                    {levels.length > 0 && (
                      <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                        <InputLabel>{t(`${K}.yourVerdict`)}</InputLabel>
                        <Select
                          label={t(`${K}.yourVerdict`)}
                          value={verdict}
                          onChange={(e) => props.onVerdict(c.criterionId, e.target.value)}
                        >
                          {levels.map((l) => (
                            <MenuItem key={l.name} value={l.name}>
                              <Tooltip title={l.description} placement="left">
                                <span>{l.name}</span>
                              </Tooltip>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <Collapse in={open}>
                      <Box mt={1}>
                        {rc?.description && rc.description !== (c.criterionLabel || rc.label) && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            mb={0.5}
                          >
                            {rc.description}
                          </Typography>
                        )}
                        {c.gap && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            <strong>{t(`${K}.gap`)}:</strong> {c.gap}
                          </Typography>
                        )}
                        {c.evidence.length > 0 && (
                          <Box mt={0.5}>
                            <Typography variant="caption" fontWeight={700} display="block">
                              {t(`${K}.evidence`)}
                            </Typography>
                            {c.evidence.map((ev, i) => (
                              <Tooltip
                                key={i}
                                title={t(`${RK}.evidenceSearchHint`)}
                                placement="left"
                              >
                                <Stack
                                  direction="row"
                                  alignItems="flex-start"
                                  spacing={0.5}
                                  onClick={() => props.onSearchInPdf(ev)}
                                  sx={{
                                    pl: 1,
                                    borderLeft: '2px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    borderRadius: 0.5,
                                    '&:hover': {
                                      bgcolor: 'action.hover',
                                      borderColor: 'primary.main',
                                    },
                                  }}
                                >
                                  <SearchRounded
                                    sx={{ fontSize: 13, mt: '2px', color: 'text.disabled' }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{ fontStyle: 'italic', flex: 1 }}
                                  >
                                    “{ev}”
                                  </Typography>
                                </Stack>
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                        <TextField
                          size="small"
                          fullWidth
                          sx={{ mt: 1 }}
                          placeholder={t(`${K}.criterionComment`)}
                          value={critComments[c.criterionId] ?? ''}
                          onChange={(e) => props.onCritComment(c.criterionId, e.target.value)}
                        />
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>
          </Box>

          {review.formativeBreakdown && (
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                {t(`${RK}.breakdownTitle`)}
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {(
                  [
                    ['breakdownAchieved', review.formativeBreakdown.achieved],
                    ['breakdownMissing', review.formativeBreakdown.missing],
                    ['breakdownNextStep', review.formativeBreakdown.nextStep],
                  ] as const
                )
                  .filter(([, v]) => v.trim())
                  .map(([labelKey, v]) => (
                    <Box key={labelKey}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        {t(`${RK}.${labelKey}`)}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {v}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </Box>
          )}

          {review.costEstimateUsd !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t(`${K}.cost`, { usd: review.costEstimateUsd.toFixed(2) })}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
